import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, StarIcon, XIcon } from 'lucide-react';
import { googleReviewTeasers, routes } from '../data/site';
import { SiteLink } from './SiteLink';
const DISMISS_KEY = 'pegasus-review-teaser-dismissed';
const ROTATE_MS = 7000;
type ReviewTeaserProps = {
  className?: string;
};
export function ReviewTeaser({
  className = ''
}: ReviewTeaserProps) {
  const [dismissed, setDismissed] = useState(true);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [announce, setAnnounce] = useState(false);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === 'true');
  }, []);
  useEffect(() => {
    if (dismissed || paused || reduceMotion || googleReviewTeasers.length < 2) return;
    const timer = window.setInterval(() => {
      setIndex(current => (current + 1) % googleReviewTeasers.length);
    }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [dismissed, paused, reduceMotion]);
  if (dismissed) return null;
  const review = googleReviewTeasers[index];
  return <div role="group" aria-label="Customer review from Google" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} className={`relative rounded-card border border-ink/10 bg-white p-4 ${className}`} data-id="element-365" data-mp="nflsvhguq8oi">
      <button type="button" title="Dismiss customer review" aria-label="Dismiss customer review" onClick={() => {
      sessionStorage.setItem(DISMISS_KEY, 'true');
      setDismissed(true);
    }} className="absolute right-1.5 top-1.5 flex h-9 w-9 items-center justify-center rounded-btn text-ink/60 transition-colors duration-150 ease-out hover:bg-water hover:text-ink" data-id="element-366" data-mp="1k41l5lbciw5b">
        <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-367" data-mp="22xyr0n6akqpg" />
      </button>

      <SiteLink to={routes.reviews} className="block pr-9" data-id="element-368" data-mp="2cqjdbo0opzmb">
        <span className="flex items-center gap-2" data-id="element-369" data-mp="28ppsj3ijh0xn">
          <span className="text-[12px] font-bold uppercase text-ink/60" data-id="element-370" data-mp="28r0tywoihwll">Google review</span>
          <span className="flex items-center gap-0.5" aria-label="Five out of five stars" data-id="element-371" data-mp="23pla37y2lw4s">
            {[0, 1, 2, 3, 4].map(star => <StarIcon key={star} className="h-3 w-3 fill-ember text-ember" aria-hidden="true" data-id="element-372" data-mp="1tsyqemhhdb4c" />)}
          </span>
        </span>

        <span className="mt-2 block min-h-[44px]" aria-live={announce ? 'polite' : 'off'} aria-atomic="true" data-id="element-373" data-mp="1l0ez673iq8tj">
          <AnimatePresence mode="wait" initial={false} data-id="element-374" data-mp="1g35348lmiywq">
            <motion.span key={index} initial={reduceMotion ? undefined : {
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={reduceMotion ? undefined : {
            opacity: 0
          }} transition={{
            duration: 0.22,
            ease: [0.23, 1, 0.32, 1]
          }} className="block" data-id="element-375" data-mp="1b30jwatbzq2t">
              <span className="block text-[15px] leading-[21px] text-ink" data-id="element-376" data-mp="10wz93qpmko0m">“{review.excerpt}”</span>
              <span className="mt-1 block text-[13px] text-ink/65" data-id="element-377" data-mp="15y6i1t3i7t8v">{review.name}</span>
            </motion.span>
          </AnimatePresence>
        </span>

        <span className="mt-2 inline-flex items-center gap-1 text-[13.5px] font-bold text-pegasus-blue" data-id="element-378" data-mp="d97yqsnnal1z">
          Read all reviews
          <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-379" data-mp="i8ub6zvnfx9q" />
        </span>
      </SiteLink>

      {googleReviewTeasers.length > 1 && <div className="absolute bottom-3 right-3 flex items-center gap-1.5" data-id="element-380" data-mp="n5b1o0qbmnpz">
          {googleReviewTeasers.map((item, itemIndex) => <button key={item.name} type="button" aria-label={`Show review from ${item.name}`} aria-current={itemIndex === index} onClick={() => {
        setAnnounce(true);
        setIndex(itemIndex);
      }} className={`h-2.5 w-2.5 rounded-full transition-colors duration-150 ease-out ${itemIndex === index ? 'bg-pegasus-magenta' : 'bg-ink/25 hover:bg-ink/45'}`} data-id="element-381" data-mp="3qonoz6yliun3" data-mpi={itemIndex} />)}
        </div>}
    </div>;
}